import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardCheck, Dumbbell, ShieldAlert, Swords, Plus } from 'lucide-react';

/**
 * Staff/Admin Quick Actions bar — surfaces the 4 most frequent daily actions
 * so they are reachable in one click from the Dashboard, not buried in menus.
 * Uses only existing routes — no new endpoints.
 */
const QuickActionsBar = ({ hasToday = false }) => {
  const navigate = useNavigate();

  const actions = [
    {
      id: 'mark-attendance',
      label: hasToday ? "Mark Today's Attendance" : 'Attendance',
      hint: hasToday ? 'Session is on today' : 'Open attendance view',
      icon: ClipboardCheck,
      tone: 'emerald',
      onClick: () => navigate('/training?tab=attendance'),
      testId: 'quick-action-attendance',
    },
    {
      id: 'new-session',
      label: 'New Training Session',
      hint: 'Schedule + target athletes',
      icon: Plus,
      tone: 'blue',
      onClick: () => navigate('/training?new=1'),
      testId: 'quick-action-new-session',
    },
    {
      id: 'log-match',
      label: 'Log Match Report',
      hint: 'Evaluation + reflection',
      icon: Swords,
      tone: 'purple',
      onClick: () => navigate('/match-reports'),
      testId: 'quick-action-log-match',
    },
    {
      id: 'log-injury',
      label: 'Injury & Recovery',
      hint: 'Log, plan, clearance',
      icon: ShieldAlert,
      tone: 'rose',
      onClick: () => navigate('/injury-recovery'),
      testId: 'quick-action-log-injury',
    },
  ];

  return (
    <div
      className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6"
      data-testid="quick-actions-bar"
    >
      {actions.map((a) => (
        <QuickActionButton key={a.id} {...a} />
      ))}
    </div>
  );
};

const TONE_STYLES = {
  emerald: {
    ring: 'hover:border-emerald-300 dark:hover:border-emerald-600',
    iconBg: 'bg-emerald-50 dark:bg-emerald-900/30',
    icon: 'text-emerald-600 dark:text-emerald-400',
    accent: 'group-hover:bg-emerald-500',
  },
  blue: {
    ring: 'hover:border-blue-300 dark:hover:border-blue-600',
    iconBg: 'bg-blue-50 dark:bg-blue-900/30',
    icon: 'text-blue-600 dark:text-blue-400',
    accent: 'group-hover:bg-blue-500',
  },
  purple: {
    ring: 'hover:border-purple-300 dark:hover:border-purple-600',
    iconBg: 'bg-purple-50 dark:bg-purple-900/30',
    icon: 'text-purple-600 dark:text-purple-400',
    accent: 'group-hover:bg-purple-500',
  },
  rose: {
    ring: 'hover:border-rose-300 dark:hover:border-rose-600',
    iconBg: 'bg-rose-50 dark:bg-rose-900/30',
    icon: 'text-rose-600 dark:text-rose-400',
    accent: 'group-hover:bg-rose-500',
  },
};

const QuickActionButton = ({ label, hint, icon: Icon, tone, onClick, testId }) => {
  const s = TONE_STYLES[tone] || TONE_STYLES.emerald;
  return (
    <button
      onClick={onClick}
      className={`group relative overflow-hidden bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl p-4 text-left transition-all hover:shadow-md ${s.ring}`}
      data-testid={testId}
    >
      <span
        className={`absolute left-0 top-0 bottom-0 w-[3px] bg-gray-100 dark:bg-gray-700 transition-colors ${s.accent}`}
        aria-hidden="true"
      />
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${s.iconBg}`}>
          <Icon className={`w-5 h-5 ${s.icon}`} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-tight">
            {label}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">{hint}</p>
        </div>
      </div>
    </button>
  );
};

export default QuickActionsBar;
